import { z } from 'zod';
// etykiety wyzwalaczy do komunikatu walidacji (plik etykiet importuje stąd tylko typy — bez cyklu w czasie działania)
import { AUTOMATION_TRIGGER_LABELS } from '../labels/automations';
import { isLocalTime } from '../time';
import { idSchema, nameSchema, versionSchema } from './common';

/**
 * Automatyzacje (Fala 10, DECISIONS 2026-10-07 [S] „Copilot ma być prawdziwym asystentem”, punkt 4): reguła =
 * wyzwalacz + filtry + akcje. Tabele `automation_rules` (reguła osoby: autor = właściciel, akcje działają z jego
 * uprawnieniami) i `automation_runs` (jedno uruchomienie na zdarzenie i regułę: UNIQUE `(ws, rule, trigger_key)`).
 *
 * Zasady:
 *  - wykonanie w workerze (`automations.tick` co minutę → uruchomienia, `automations.execute` → akcje), idempotentne,
 *    z limitami (anty-burza powiadomień); błędy widoczne w historii reguły,
 *  - „Powiadom mnie” = pozycja Inbox + push wg ustawień osoby + opcjonalnie mail NA WŁASNY adres przez pocztę
 *    systemową (to nie jest mail do klienta, więc bez Akceptacji),
 *  - „Zleć Claude” tylko z jawną zgodą owner/admin w tej regule (step-up), z dobowym limitem; zmiana wyzwalacza,
 *    filtrów albo zlecenia unieważnia zgodę,
 *  - treść maili i innych danych z zewnątrz to dane niezaufane: nie zmienia akcji reguły, nie trafia do zlecenia
 *    dla Claude (tylko zaufane pola: zasób, klient, adres monitora…), a do AI idzie w bloku danych.
 */

// ── Wyzwalacze ──────────────────────────────────────────────────────────────

export const AUTOMATION_TRIGGERS = [
  'incident.opened',
  'incident.resolved',
  'message.urgent',
  'renewal.due',
  'task.overdue',
  'schedule.daily',
] as const;
export type AutomationTriggerType = (typeof AUTOMATION_TRIGGERS)[number];

/** Wyzwalacze ze zdarzeń (`events`), przeglądane od kursora reguły. */
export const AUTOMATION_EVENT_TRIGGERS = {
  'incident.opened': 'monitor.down',
  'incident.resolved': 'monitor.up',
  'message.urgent': 'message.received',
} as const satisfies Partial<Record<AutomationTriggerType, string>>;
export type AutomationEventTrigger = keyof typeof AUTOMATION_EVENT_TRIGGERS;

export function isEventTrigger(t: AutomationTriggerType): t is AutomationEventTrigger {
  return t in AUTOMATION_EVENT_TRIGGERS;
}

const idListSchema = z
  .array(idSchema)
  .max(50, { message: 'Najwyżej 50 pozycji' })
  .default([])
  .transform((list) => [...new Set(list)]);

export const AUTOMATION_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const automationTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('incident.opened'), assetIds: idListSchema, clientIds: idListSchema }).strict(),
  z.object({ type: z.literal('incident.resolved'), assetIds: idListSchema, clientIds: idListSchema }).strict(),
  z.object({ type: z.literal('message.urgent'), clientIds: idListSchema }).strict(),
  z
    .object({
      type: z.literal('renewal.due'),
      daysBefore: z.number().int().min(0).max(90).default(14),
      clientIds: idListSchema,
    })
    .strict(),
  z.object({ type: z.literal('task.overdue'), onlyMine: z.boolean().default(true) }).strict(),
  z
    .object({
      type: z.literal('schedule.daily'),
      time: z.string().refine(isLocalTime, { message: 'Godzina w formacie HH:MM' }),
      weekdaysOnly: z.boolean().default(false),
    })
    .strict(),
]);
export type AutomationTrigger = z.output<typeof automationTriggerSchema>;
export type AutomationTriggerInput = z.input<typeof automationTriggerSchema>;

// ── Akcje ───────────────────────────────────────────────────────────────────

export const AUTOMATION_ACTION_TYPES = ['summary', 'brief', 'create_task', 'delegate_claude', 'notify'] as const;
export type AutomationActionType = (typeof AUTOMATION_ACTION_TYPES)[number];

/** Kolejność wykonania (podsumowanie przed powiadomieniem, zadanie i zlecenie — link w powiadomieniu). */
export const AUTOMATION_ACTION_ORDER: Readonly<Record<AutomationActionType, number>> = {
  summary: 0,
  brief: 1,
  create_task: 2,
  delegate_claude: 3,
  notify: 4,
};

export const AUTOMATION_LIMITS = {
  /** reguły jednej osoby (bez archiwum) */
  rulesPerOwner: 30,
  /** uruchomienia jednej reguły na godzinę — ponad limit uruchomienie jest pomijane (anty-burza) */
  runsPerRulePerHour: 20,
  /** powiadomienia jednej reguły na godzinę — ponad limit pozycja Inbox i push są pomijane */
  notificationsPerRulePerHour: 6,
  /** zlecenia Claude z jednej reguły na dobę (24 h): domyślnie i najwyżej */
  claudePerDayDefault: 3,
  claudePerDayMax: 10,
  /** nowe uruchomienia jednej reguły w jednym przebiegu workera (reszta w następnej minucie) */
  newRunsPerTick: 10,
  /** starszych zdarzeń nie nadrabiamy (np. po długiej przerwie workera) */
  maxEventAgeHours: 24,
  /** harmonogram: uruchomienie najpóźniej tyle minut po godzinie (worker wyłączony dłużej = pominięty dzień) */
  scheduleWindowMin: 120,
  /** zadania po terminie: najwyżej tyle uruchomień jednej reguły na dobę */
  overduePerDay: 10,
  titleMax: 200,
  instructionMax: 2000,
  summaryMax: 1500,
} as const;

export const createTaskActionSchema = z
  .object({
    type: z.literal('create_task'),
    title: z
      .string()
      .trim()
      .min(1, { message: 'Podaj tytuł zadania' })
      .max(AUTOMATION_LIMITS.titleMax, { message: `Najwyżej ${AUTOMATION_LIMITS.titleMax} znaków` }),
    assignToMe: z.boolean().default(true),
    /** termin: dziś + N dni (null = bez terminu) */
    dueInDays: z.number().int().min(0).max(60).nullable().default(0),
    priority: z.enum(['normal', 'high', 'urgent']).default('high'),
  })
  .strict();

export const delegateClaudeActionSchema = z
  .object({
    type: z.literal('delegate_claude'),
    instruction: z
      .string()
      .trim()
      .min(10, { message: 'Opisz zlecenie (co najmniej 10 znaków)' })
      .max(AUTOMATION_LIMITS.instructionMax, { message: `Najwyżej ${AUTOMATION_LIMITS.instructionMax} znaków` }),
    /** limit zleceń na dobę z tej reguły */
    dailyLimit: z
      .number()
      .int()
      .min(1)
      .max(AUTOMATION_LIMITS.claudePerDayMax)
      .default(AUTOMATION_LIMITS.claudePerDayDefault),
    /** projekt z repozytorium (lokalny runner); brak = projekt zasobu albo sesja Claude (MCP) */
    projectId: idSchema.nullable().default(null),
  })
  .strict();

export const automationActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('summary') }).strict(),
  z.object({ type: z.literal('brief') }).strict(),
  createTaskActionSchema,
  delegateClaudeActionSchema,
  z
    .object({
      type: z.literal('notify'),
      /** Pilne: push wg ustawień (godziny ciszy); Zwykłe: pozycja Inbox, push tylko gdy zaznaczono */
      urgent: z.boolean().default(false),
      push: z.boolean().default(true),
      /** mail na mój adres (poczta systemowa, bez Akceptacji — nie do klienta) */
      email: z.boolean().default(false),
    })
    .strict(),
]);
export type AutomationAction = z.output<typeof automationActionSchema>;
export type AutomationActionInput = z.input<typeof automationActionSchema>;

// ── Szablony treści: {zasób}, {klient}… ─────────────────────────────────────

/**
 * Pola szablonów. `trusted: false` = tekst z zewnątrz (temat i nadawca maila, tytuł zadania — bywa z maila): wolno
 * go użyć w tytule zadania (dane), ale NIE w zleceniu dla Claude (prompt injection).
 */
export const AUTOMATION_PLACEHOLDERS = {
  zasób: { trusted: true, label: 'zasób albo monitor' },
  klient: { trusted: true, label: 'klient' },
  adres: { trusted: true, label: 'adres monitora' },
  przyczyna: { trusted: true, label: 'przyczyna awarii' },
  od: { trusted: true, label: 'od kiedy' },
  odnowienie: { trusted: true, label: 'co się odnawia' },
  termin: { trusted: true, label: 'termin' },
  dni: { trusted: true, label: 'dni do terminu' },
  data: { trusted: true, label: 'dzisiejsza data' },
  temat: { trusted: false, label: 'temat maila' },
  nadawca: { trusted: false, label: 'nadawca maila' },
  zadanie: { trusted: false, label: 'zadanie' },
} as const;
export type AutomationPlaceholder = keyof typeof AUTOMATION_PLACEHOLDERS;
export type AutomationVars = Partial<Record<AutomationPlaceholder, string | null>>;

const PLACEHOLDER_RE = /\{([a-ząćęłńóśźż ]{1,20})\}/gi;

function canonicalPlaceholder(raw: string): AutomationPlaceholder | null {
  const key = raw.trim().toLowerCase().replace('zasob', 'zasób');
  return key in AUTOMATION_PLACEHOLDERS ? (key as AutomationPlaceholder) : null;
}

/** Pola użyte w szablonie (`{zasob}` = `{zasób}`); nieznane — osobno. */
export function templatePlaceholders(template: string): { known: AutomationPlaceholder[]; unknown: string[] } {
  const known = new Set<AutomationPlaceholder>();
  const unknown = new Set<string>();
  for (const m of template.matchAll(PLACEHOLDER_RE)) {
    const key = canonicalPlaceholder(m[1] ?? '');
    if (key) known.add(key);
    else unknown.add(m[1] ?? '');
  }
  return { known: [...known], unknown: [...unknown] };
}

/** Jedna linia, bez znaków sterujących, przycięta (tekst z zewnątrz w tytule zadania / powiadomieniu). */
export function oneLine(value: string, max = 200): string {
  const flat = value
    // biome-ignore lint/suspicious/noControlCharactersInRegex: celowo usuwamy znaki sterujące z tekstu z zewnątrz
    .replace(/[\u0000-\u001f\u007f\u2028\u2029]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

/**
 * Wypełnienie szablonu. `trustedOnly` (zlecenie dla Claude): pola niezaufane zostają puste („—”), nawet gdyby
 * walidacja reguły je przepuściła. Brak wartości = „—”.
 */
export function renderAutomationTemplate(
  template: string,
  vars: AutomationVars,
  opts: { trustedOnly?: boolean; max?: number } = {},
): string {
  const out = template.replace(PLACEHOLDER_RE, (whole, raw: string) => {
    const key = canonicalPlaceholder(raw);
    if (!key) return whole;
    if (opts.trustedOnly && !AUTOMATION_PLACEHOLDERS[key].trusted) return '—';
    const v = vars[key];
    return v ? oneLine(v, 200) : '—';
  });
  return opts.max ? out.slice(0, opts.max) : out;
}

/** Pola dostępne dla wyzwalacza (podpowiedzi w edytorze i walidacja). */
export const AUTOMATION_TRIGGER_PLACEHOLDERS: Readonly<
  Record<AutomationTriggerType, readonly AutomationPlaceholder[]>
> = {
  'incident.opened': ['zasób', 'klient', 'adres', 'przyczyna', 'od', 'data'],
  'incident.resolved': ['zasób', 'klient', 'adres', 'od', 'data'],
  'message.urgent': ['klient', 'temat', 'nadawca', 'data'],
  'renewal.due': ['odnowienie', 'klient', 'termin', 'dni', 'data'],
  'task.overdue': ['zadanie', 'klient', 'termin', 'data'],
  'schedule.daily': ['data'],
};

// ── Reguła: wejścia ─────────────────────────────────────────────────────────

export const AUTOMATION_MESSAGES = {
  notFound: 'Nie znaleziono reguły',
  notYours: 'To reguła innej osoby — zmienia ją jej autor',
  limitRules: `Masz już ${AUTOMATION_LIMITS.rulesPerOwner} reguł — usuń nieużywane`,
  noActions: 'Dodaj co najmniej jedną akcję',
  duplicateAction: 'Każda akcja może wystąpić raz',
  summaryNeedsNotify: 'Podsumowanie trafia do powiadomienia — dodaj „Powiadom mnie”',
  briefOnlySchedule: 'Poranny brief działa tylko z harmonogramem',
  briefNeedsNotify: 'Brief trafia do powiadomienia — dodaj „Powiadom mnie”',
  summaryNotForSchedule: 'Do harmonogramu użyj „Poranny brief”',
  untrustedInClaude:
    'Zlecenie dla Claude nie może zawierać treści z maila ani tytułu zadania ({temat}, {nadawca}, {zadanie}) — to dane z zewnątrz',
  unknownPlaceholder: 'Nieznane pole w szablonie',
  consentAdminsOnly: 'Zgodę na zlecanie pracy Claude daje tylko właściciel albo administrator',
  consentNoClaude: 'Ta reguła nie zleca pracy Claude',
  claudeNoConsent: 'Pominięto: brak zgody właściciela na zlecanie pracy Claude w tej regule',
  claudeConsentStale: 'Pominięto: reguła zmieniła się po udzieleniu zgody — potrzebna nowa zgoda',
  claudeLimit: (n: number) => `Pominięto: limit ${n} zleceń Claude na dobę z tej reguły`,
  notifyLimit: `Pominięto: limit ${AUTOMATION_LIMITS.notificationsPerRulePerHour} powiadomień na godzinę z tej reguły`,
  runLimit: `Pominięto: limit ${AUTOMATION_LIMITS.runsPerRulePerHour} uruchomień na godzinę (ochrona przed burzą zdarzeń)`,
  ownerInactive: 'Autor reguły nie ma już aktywnego dostępu do workspace’u',
  archived: 'Reguła jest w archiwum',
  emailUnavailable: 'Poczta systemowa nie jest skonfigurowana — mail nie wyszedł',
  emailUncertain: 'Nie wiadomo, czy mail wyszedł (przerwane wysyłanie) — bez ponownej wysyłki',
  aiFallback: 'AI niedostępne — podsumowanie z faktów, bez AI',
  runNowOnlySchedule: 'Ręczne uruchomienie działa tylko dla reguł z harmonogramem',
  privateTrigger: 'Mail z prywatnej skrzynki autora reguły',
  privateHidden: 'Szczegóły ukryte — prywatna poczta autora reguły',
  placeholderNotForTrigger: (placeholder: string, trigger: string) =>
    `Pole {${placeholder}} nie działa z wyzwalaczem „${trigger}” — wstaw pole z listy pod polem`,
} as const;

export interface AutomationRuleIssue {
  path: (string | number)[];
  message: string;
}

/**
 * Spójność reguły (wyzwalacz + akcje): walidacja wejścia i usługa (edycja samych akcji sprawdza je z zapisanym
 * wyzwalaczem). Pusta lista = reguła poprawna.
 */
export function automationRuleIssues(v: {
  /** null = nieznany (edycja samych akcji) — zależności od wyzwalacza pominięte */
  trigger: Pick<AutomationTrigger, 'type'> | null;
  actions: readonly AutomationAction[];
}): AutomationRuleIssue[] {
  const issues: AutomationRuleIssue[] = [];
  const { actions } = v;
  const types = actions.map((a) => a.type);
  const issue = (path: (string | number)[], message: string) => issues.push({ path, message });
  if (actions.length === 0) issue(['actions'], AUTOMATION_MESSAGES.noActions);
  if (new Set(types).size !== types.length) issue(['actions'], AUTOMATION_MESSAGES.duplicateAction);
  const trigger = v.trigger?.type ?? null;
  const has = (t: AutomationActionType) => types.includes(t);
  if (trigger && has('brief') && trigger !== 'schedule.daily') {
    issue(['actions'], AUTOMATION_MESSAGES.briefOnlySchedule);
  }
  if (trigger === 'schedule.daily' && has('summary')) issue(['actions'], AUTOMATION_MESSAGES.summaryNotForSchedule);
  if (has('summary') && !has('notify')) issue(['actions'], AUTOMATION_MESSAGES.summaryNeedsNotify);
  if (has('brief') && !has('notify')) issue(['actions'], AUTOMATION_MESSAGES.briefNeedsNotify);
  actions.forEach((a, i) => {
    const template = a.type === 'create_task' ? a.title : a.type === 'delegate_claude' ? a.instruction : null;
    if (template === null) return;
    const field = a.type === 'create_task' ? 'title' : 'instruction';
    const { known, unknown } = templatePlaceholders(template);
    if (unknown.length > 0) issue(['actions', i, field], `${AUTOMATION_MESSAGES.unknownPlaceholder}: {${unknown[0]}}`);
    // Przegląd Fali 10: pole, którego wyzwalacz nie zna (np. {zasób} przy „Codziennie o godzinie”), dawało „—”
    const foreign = trigger ? known.filter((k) => !AUTOMATION_TRIGGER_PLACEHOLDERS[trigger].includes(k)) : [];
    if (trigger && foreign[0]) {
      issue(
        ['actions', i, field],
        AUTOMATION_MESSAGES.placeholderNotForTrigger(foreign[0], AUTOMATION_TRIGGER_LABELS[trigger]),
      );
    }
    if (a.type === 'delegate_claude' && known.some((k) => !AUTOMATION_PLACEHOLDERS[k].trusted)) {
      issue(['actions', i, field], AUTOMATION_MESSAGES.untrustedInClaude);
    }
  });
  return issues;
}

function refineRule(
  v: { trigger?: AutomationTrigger | undefined; actions?: AutomationAction[] | undefined },
  ctx: z.RefinementCtx,
): void {
  if (!v.actions) return;
  // edycja samych akcji: zależności od wyzwalacza sprawdza usługa (z zapisanym wyzwalaczem)
  for (const i of automationRuleIssues({ trigger: v.trigger ?? null, actions: v.actions })) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: i.path, message: i.message });
  }
}

const actionsSchema = z
  .array(automationActionSchema)
  .min(1, { message: AUTOMATION_MESSAGES.noActions })
  .max(AUTOMATION_ACTION_TYPES.length);

export const createAutomationRuleInput = z
  .object({
    name: nameSchema,
    trigger: automationTriggerSchema,
    actions: actionsSchema,
    enabled: z.boolean().default(true),
  })
  .strict()
  .superRefine(refineRule);
export type CreateAutomationRuleInput = z.input<typeof createAutomationRuleInput>;

export const updateAutomationRuleInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    name: nameSchema.optional(),
    trigger: automationTriggerSchema.optional(),
    actions: actionsSchema.optional(),
  })
  .strict()
  .superRefine(refineRule);
export type UpdateAutomationRuleInput = z.input<typeof updateAutomationRuleInput>;

export const automationRuleRefInput = z.object({ id: idSchema }).strict();
export const setAutomationRuleEnabledInput = z.object({ id: idSchema, enabled: z.boolean() }).strict();
export const setAutomationClaudeConsentInput = z.object({ id: idSchema, allowed: z.boolean() }).strict();
export const listAutomationRulesInput = z
  .object({ includeArchived: z.boolean().default(false), all: z.boolean().default(false) })
  .strict()
  .default({});
export const listAutomationRunsInput = z
  .object({ ruleId: idSchema, limit: z.number().int().min(1).max(100).default(20) })
  .strict();

// ── Gotowe szablony („włącz jednym kliknięciem”) ────────────────────────────

export const AUTOMATION_TEMPLATE_KEYS = ['incident_claude', 'morning_brief', 'urgent_mail_task', 'renewal_14'] as const;
export type AutomationTemplateKey = (typeof AUTOMATION_TEMPLATE_KEYS)[number];

export const DIAGNOSE_INSTRUCTION =
  'Zdiagnozuj, dlaczego {zasób} nie odpowiada (adres: {adres}, od {od}, przyczyna: {przyczyna}). Tylko diagnoza i raport, bez wdrożeń i bez zmian na serwerze.';

/**
 * Domyślny tytuł zadania i instrukcja dla Claude dla każdego wyzwalacza (Przegląd Fali 10: edytor reguły podmienia je
 * przy zmianie wyzwalacza, jeśli osoba ich nie zmieniła — wcześniej „Codziennie o godzinie” zostawał z „Sprawdź:
 * {zasób}” i tworzył zadanie „Sprawdź: —”). Tylko pola z `AUTOMATION_TRIGGER_PLACEHOLDERS[wyzwalacz]`, w instrukcji
 * tylko pola zaufane.
 */
export const AUTOMATION_TRIGGER_DEFAULTS: Readonly<
  Record<AutomationTriggerType, { taskTitle: string; instruction: string }>
> = {
  'incident.opened': { taskTitle: 'Sprawdź: {zasób}', instruction: DIAGNOSE_INSTRUCTION },
  'incident.resolved': {
    taskTitle: 'Po awarii: sprawdź {zasób}',
    instruction:
      'Sprawdź, czy {zasób} działa poprawnie po awarii (adres: {adres}, awaria od {od}). Tylko diagnoza i raport, bez wdrożeń i bez zmian na serwerze.',
  },
  'message.urgent': {
    taskTitle: 'Pilny mail: {temat}',
    instruction:
      'Klient {klient} zgłosił pilną sprawę ({data}). Sprawdź, czy jego strony i serwery działają. Tylko diagnoza i raport, bez wdrożeń i bez zmian na serwerze.',
  },
  'renewal.due': {
    taskTitle: 'Odnowienie: {odnowienie} ({termin})',
    instruction:
      'Przed odnowieniem {odnowienie} klienta {klient} (termin {termin}) sprawdź, czy usługa działa i jest używana. Tylko raport, bez zmian.',
  },
  'task.overdue': {
    taskTitle: 'Po terminie: {zadanie}',
    instruction:
      'Klient {klient}: zadanie minęło termin {termin}. Przygotuj krótki raport stanu prac w repozytorium. Tylko raport, bez zmian.',
  },
  'schedule.daily': {
    taskTitle: 'Przegląd dnia {data}',
    instruction:
      'Przegląd techniczny {data}: sprawdź stan monitoringu i ostatnie błędy. Tylko raport, bez wdrożeń i bez zmian na serwerze.',
  },
};

export interface AutomationTemplate {
  key: AutomationTemplateKey;
  title: string;
  description: string;
  name: string;
  trigger: AutomationTriggerInput;
  actions: AutomationActionInput[];
}

export const AUTOMATION_TEMPLATES: readonly AutomationTemplate[] = [
  {
    key: 'incident_claude',
    title: 'Awaria strony → powiadom mnie + Claude diagnoza',
    description:
      'Gdy monitor wykryje awarię: pilne powiadomienie z podsumowaniem (od kiedy, ostatnie maile klienta, przebiegi agentów) i zlecenie diagnozy dla Claude — tylko po Twojej zgodzie.',
    name: 'Awaria strony → powiadomienie + diagnoza Claude',
    trigger: { type: 'incident.opened' },
    actions: [
      { type: 'summary' },
      { type: 'delegate_claude', instruction: DIAGNOSE_INSTRUCTION, dailyLimit: 3 },
      { type: 'notify', urgent: true, push: true, email: false },
    ],
  },
  {
    key: 'morning_brief',
    title: 'Poranny brief o 8:00',
    description: 'Codziennie o 8:00 podsumowanie dnia od Copilota: zadania, akceptacje, alarmy — w Inboxie i push.',
    name: 'Poranny brief',
    trigger: { type: 'schedule.daily', time: '08:00', weekdaysOnly: false },
    actions: [{ type: 'brief' }, { type: 'notify', urgent: false, push: true, email: false }],
  },
  {
    key: 'urgent_mail_task',
    title: 'Pilny mail od klienta → zadanie + powiadomienie',
    description: 'Mail oznaczony jako Pilne: zadanie na dziś dla Ciebie i pilne powiadomienie.',
    name: 'Pilny mail klienta → zadanie',
    trigger: { type: 'message.urgent' },
    actions: [
      { type: 'create_task', title: 'Pilny mail: {temat}', assignToMe: true, dueInDays: 0, priority: 'urgent' },
      { type: 'notify', urgent: true, push: true, email: false },
    ],
  },
  {
    key: 'renewal_14',
    title: 'Odnowienie za 14 dni → przypomnij mi',
    description: '14 dni przed terminem płatności albo odnowienia u dostawcy: przypomnienie w Inboxie.',
    name: 'Odnowienie za 14 dni',
    trigger: { type: 'renewal.due', daysBefore: 14 },
    actions: [{ type: 'notify', urgent: false, push: false, email: false }],
  },
];

export function automationTemplate(key: AutomationTemplateKey): AutomationTemplate {
  const t = AUTOMATION_TEMPLATES.find((x) => x.key === key);
  if (!t) throw new Error(`Nieznany szablon automatyzacji: ${key}`);
  return t;
}

export const enableAutomationTemplateInput = z.object({ key: z.enum(AUTOMATION_TEMPLATE_KEYS) }).strict();

// ── Uruchomienia ────────────────────────────────────────────────────────────

export const AUTOMATION_RUN_STATUSES = ['queued', 'running', 'succeeded', 'partial', 'failed', 'skipped'] as const;
export type AutomationRunStatus = (typeof AUTOMATION_RUN_STATUSES)[number];

/** Stan pojedynczej akcji w uruchomieniu (`automation_runs.actions`). */
export const AUTOMATION_ACTION_STATES = ['pending', 'started', 'done', 'skipped', 'failed', 'uncertain'] as const;
export type AutomationActionState = (typeof AUTOMATION_ACTION_STATES)[number];

export interface AutomationActionResult {
  type: AutomationActionType;
  /** krok akcji: `email` = mail na mój adres w „Powiadom mnie” (osobno: wysyłka najwyżej raz) */
  step?: 'email';
  state: AutomationActionState;
  /** komunikat po polsku (wynik, powód pominięcia, błąd) */
  message: string | null;
  /** utworzony rekord: zadanie, przebieg Claude, zdarzenie (Inbox), zadanie AI */
  ref: { type: 'task' | 'agent_run' | 'event' | 'ai_job'; id: string } | null;
  /** tekst podsumowania / briefu (do powiadomienia) */
  text?: string | null;
  simulated?: boolean;
  /** podsumowanie użyło maili z prywatnej skrzynki osobistej (tekst widzi tylko autor reguły) */
  privateMail?: boolean;
  at: string | null;
}

/** Kontekst wyzwalacza zapisany w uruchomieniu (bez treści maili — tylko odnośniki i pola zaufane). */
export interface AutomationTriggerRef {
  label: string;
  eventId?: string | null;
  incidentId?: string | null;
  monitorId?: string | null;
  assetId?: string | null;
  clientId?: string | null;
  messageId?: string | null;
  threadId?: string | null;
  taskId?: string | null;
  renewalKind?: 'service' | 'supplier_renewal' | null;
  renewalId?: string | null;
  dueDate?: string | null;
  date?: string | null;
  manual?: boolean;
  /**
   * wyzwalacz z prywatnego wątku skrzynki osobistej (np. pilny mail w „Twoje”): temat w `label` widzi tylko autor
   * reguły — innym osobom (Historia AI, reguły zespołu) `AUTOMATION_MESSAGES.privateTrigger`
   */
  privateMail?: boolean;
}

/**
 * Uruchomienie z prywatną pocztą autora (Przegląd Fali 10, DECISIONS [P] „Skrzynka osobista jest prywatna”):
 * wyzwalacz z prywatnego wątku skrzynki osobistej albo podsumowanie AI z takich maili.
 */
export function automationRunIsPrivate(run: {
  trigger: Pick<AutomationTriggerRef, 'privateMail'>;
  actions: readonly Pick<AutomationActionResult, 'privateMail'>[];
}): boolean {
  return Boolean(run.trigger.privateMail) || run.actions.some((a) => a.privateMail);
}

/**
 * Uruchomienie dla osoby innej niż autor reguły (owner / admin w „Także reguły zespołu”): bez tematu prywatnego maila
 * i bez tekstów, które mogły go zawierać (podsumowanie AI, tytuł zadania z `{temat}`). Odnośniki zostają — rekordy
 * i tak sprawdza zakres osoby.
 */
export function redactAutomationRun<
  T extends { trigger: AutomationTriggerRef; actions: AutomationActionResult[]; error?: string | null },
>(run: T): T {
  if (!automationRunIsPrivate(run)) return run;
  const triggerPrivate = Boolean(run.trigger.privateMail);
  return {
    ...run,
    trigger: triggerPrivate ? { ...run.trigger, label: AUTOMATION_MESSAGES.privateTrigger } : run.trigger,
    actions: run.actions.map((a) => {
      const hideText = a.text != null && (a.privateMail || triggerPrivate);
      const hideMessage = triggerPrivate && a.type === 'create_task' && a.state === 'done';
      if (!hideText && !hideMessage) return a;
      return {
        ...a,
        ...(hideText ? { text: null } : {}),
        message: hideText || hideMessage ? AUTOMATION_MESSAGES.privateHidden : a.message,
      };
    }),
  };
}

export const AUTOMATION_QUEUES = { tick: 'automations.tick', execute: 'automations.execute' } as const;

/**
 * Zdarzenie „Powiadom mnie” (Inbox autora reguły): `notify` — Pilne (push wg ustawień, Critical) albo Zwykłe bez push;
 * `notifyPush` — Zwykłe z prośbą o push (`PUSH_REQUESTED_EVENT_TYPES`: push wg ustawień, bez przełamywania ciszy).
 */
export const AUTOMATION_EVENT_TYPES = {
  notify: 'automation.notify',
  notifyPush: 'automation.notify_push',
} as const;

/** Odnośnik zdarzenia do reguły (`events.links`). */
export const AUTOMATION_LINK_TYPE = 'automation_rule';

/** Ścieżka SPA reguły (Ustawienia → Automatyzacje, otwarta historia reguły). */
export function automationRulePath(ruleId: string): string {
  return `/ustawienia/automatyzacje?regula=${ruleId}`;
}

export function automationNotifyDedupKey(runId: string): string {
  return `automation:${runId}:notify`;
}

/** Hash zgody na Claude: wyzwalacz, filtry i treść zlecenia (zmiana = zgoda nieaktualna). */
export function claudeConsentMaterial(rule: { trigger: AutomationTrigger; actions: AutomationAction[] }): unknown {
  const claude = rule.actions.find((a) => a.type === 'delegate_claude') ?? null;
  return { trigger: rule.trigger, claude };
}

export function ruleUsesClaude(actions: readonly AutomationAction[]): boolean {
  return actions.some((a) => a.type === 'delegate_claude');
}

// ── DTO ─────────────────────────────────────────────────────────────────────

export interface AutomationRuleDto {
  id: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  templateKey: AutomationTemplateKey | null;
  owner: { id: string; name: string };
  /** reguła osoby wywołującej (może ją zmieniać) */
  isMine: boolean;
  claude: {
    /** reguła ma akcję „Zleć Claude” */
    required: boolean;
    /** zgoda udzielona i aktualna */
    allowed: boolean;
    /** zgoda była, ale reguła się zmieniła */
    stale: boolean;
    allowedAt: string | null;
    allowedBy: string | null;
  };
  /** owner/admin: może dać zgodę (step-up) */
  canConsent: boolean;
  lastRun: { id: string; status: AutomationRunStatus; at: string; label: string } | null;
  version: number;
  createdAt: string;
  archivedAt: string | null;
}

export interface AutomationRunDto {
  id: string;
  ruleId: string;
  status: AutomationRunStatus;
  triggerType: AutomationTriggerType;
  trigger: AutomationTriggerRef;
  actions: AutomationActionResult[];
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface AutomationTemplateDto extends AutomationTemplate {
  /** reguła z tego szablonu już istnieje (osoby) */
  ruleId: string | null;
  usesClaude: boolean;
}
